import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Filter, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { ProgressRing } from '../components/Documents/ProgressRing';
import { DocumentCategory } from '../components/Documents/DocumentCategory';
import { useDocumentStore } from '../store/documentStore';
import { useProjectStore } from '../store/projectStore';
import { useAuthStore } from '../store/authStore';
import { ProfileType } from '../types';

export const ProjectDocumentsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentProject, fetchProject } = useProjectStore();
  const {
    manifest,
    documents,
    completion,
    isLoading,
    error,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    verifyDocument,
    verifyAllPendingDocuments,
    submitForVerification
  } = useDocumentStore();

  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProject(id);
      fetchDocuments(id);
    }
  }, [id]);

  const canManage = user && [ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType);
  const canSubmit = user?.profile_type === ProfileType.PORTEUR && completion?.percent === 100;

  const handleBack = () => {
    navigate(`/projects/${id}`);
  };

  const handleUpload = async (categoryCode: string, key: string, file: File) => {
    if (!id) return;
    await uploadDocument(id, categoryCode, key, file);
  };

  const handleDelete = async (documentId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      await deleteDocument(documentId);
    }
  };

  const handleVerify = async (documentId: string, status: any, note?: string) => {
    await verifyDocument(documentId, status, note);
  };

  const handleVerifyAllPending = async () => {
    if (!id) return;

    const pendingCount = documents.filter(d => d.status === 'UPLOADED').length;

    if (pendingCount === 0) {
      alert('Aucun document en attente de validation.');
      return;
    }

    if (confirm(`Êtes-vous sûr de vouloir valider tous les ${pendingCount} document(s) en attente de ce projet ?\n\nCette action validera tous les documents déposés sans note individuelle.`)) {
      try {
        await verifyAllPendingDocuments(id);
        alert(`${pendingCount} document(s) validé(s) avec succès !`);
        await fetchDocuments(id); // Refresh the list
      } catch (error) {
        console.error('Error validating all pending documents:', error);
        alert('Erreur lors de la validation en masse. Veuillez réessayer.');
      }
    }
  };

  const handleSubmitForVerification = async () => {
    if (!id || !canSubmit) return;
    
    setIsSubmitting(true);
    try {
      await submitForVerification(id);
      // Show success message or redirect
      alert('Documents soumis pour vérification avec succès !');
    } catch (error) {
      console.error('Error submitting for verification:', error);
      alert('Erreur lors de la soumission');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentProject) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const requiredCategories = manifest.categories.filter(cat => cat.required);
  const optionalCategories = manifest.categories.filter(cat => !cat.required);

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour au projet</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Documents du projet</h1>
              <p className="text-gray-600">{currentProject.name}</p>
            </div>
            
            <div className="flex items-center space-x-4">
              {completion && (
                <div className="flex items-center space-x-3">
                  <ProgressRing percent={completion.percent} />
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {completion.requiredDone}/{completion.requiredTotal} requis
                    </div>
                    <div className="text-xs text-gray-500">
                      {completion.optionalDone} optionnels
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowOnlyMissing(!showOnlyMissing)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                showOnlyMissing 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Afficher uniquement les manquants</span>
            </button>
          </div>

          {/* Completion Status */}
          {completion && (
            <div className="flex items-center space-x-2">
              {completion.percent === 100 ? (
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Tous les documents requis sont déposés</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-orange-600">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {completion.requiredTotal - completion.requiredDone} document(s) requis manquant(s)
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Quick Actions for Managers */}
        {canManage && documents.filter(d => d.status === 'UPLOADED').length > 0 && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">
                    {documents.filter(d => d.status === 'UPLOADED').length} document(s) en attente de validation
                  </p>
                  <p className="text-sm text-green-700">
                    Vous pouvez valider tous les documents déposés en une seule fois
                  </p>
                </div>
              </div>
              <button
                onClick={handleVerifyAllPending}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Valider tous les documents en attente</span>
              </button>
            </div>
          </div>
        )}

        {/* Required Documents */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Documents obligatoires</h2>
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
              Requis
            </span>
          </div>
          
          <div className="space-y-4">
            {requiredCategories.map((category) => (
              <DocumentCategory
                key={category.code}
                category={category}
                documents={documents}
                projectId={id}
                projectOwnerId={currentProject.owner_id}
                projectName={currentProject.name}
                onUpload={handleUpload}
                onDelete={handleDelete}
                onVerify={handleVerify}
                isUploading={isLoading}
                canManage={canManage}
                showOnlyMissing={showOnlyMissing}
              />
            ))}
          </div>
        </div>

        {/* Optional Documents */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Documents optionnels</h2>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
              Optionnel
            </span>
          </div>

          <div className="space-y-4">
            {optionalCategories.map((category) => (
              <DocumentCategory
                key={category.code}
                category={category}
                documents={documents}
                projectId={id}
                projectOwnerId={currentProject.owner_id}
                projectName={currentProject.name}
                onUpload={handleUpload}
                onDelete={handleDelete}
                onVerify={handleVerify}
                isUploading={isLoading}
                canManage={canManage}
                showOnlyMissing={showOnlyMissing}
              />
            ))}
          </div>
        </div>

        {/* Submit Button (Sticky Footer) */}
        {user?.profile_type === ProfileType.PORTEUR && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -mx-6">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div>
                {completion && completion.percent < 100 && (
                  <div className="flex items-center space-x-2 text-orange-600">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">
                      Complétez tous les documents obligatoires pour pouvoir soumettre
                    </span>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleSubmitForVerification}
                disabled={!canSubmit || isSubmitting}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Soumission...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Soumettre pour vérification</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};